/**
 * Timezone utility functions for handling timezone detection and display
 */

/**
 * Get the user's current timezone from their browser
 * @returns IANA timezone string (e.g., "America/New_York")
 */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.error("Failed to detect browser timezone:", error);
    // Fallback to East Coast (New York) as default
    return "America/New_York";
  }
}

/**
 * Timezone option for dropdown selection
 * Following NN Group UX recommendations: city names are most effective,
 * include country/region context, and show GMT offset for confirmation
 */
export interface TimezoneOption {
  value: string;
  /** Display label: "City, Country/Region" */
  label: string;
  /** Full city name for searching */
  city: string;
  /** Country or region for searching and context */
  country: string;
  /** Official timezone abbreviation (e.g., EST, CST, PST) */
  abbr: string;
  /** GMT offset string (e.g., "GMT-5") - dynamically calculated */
  gmtOffset: string;
  /** Searchable keywords (city aliases, state names, etc.) */
  keywords: string[];
}

/**
 * Get the current GMT offset for a timezone
 * @param timezone IANA timezone string
 * @returns GMT offset string like "GMT-5" or "GMT+5:30"
 */
export function getGmtOffset(timezone: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find((p) => p.type === "timeZoneName");
    if (offsetPart) {
      // Convert "GMT-5" format to clean display
      return offsetPart.value;
    }
    return "";
  } catch {
    return "";
  }
}

/**
 * Raw timezone data without computed GMT offsets
 */
interface RawTimezoneData {
  value: string;
  city: string;
  country: string;
  /** Official timezone abbreviation (e.g., EST, CST, PST) */
  abbr: string;
  keywords: string[];
}

const rawTimezoneData: RawTimezoneData[] = [
  // United States - alphabetical by city
  // Keywords include state names, abbreviations, and regional time zone names
  { value: "America/Anchorage", city: "Anchorage", country: "USA", abbr: "AKST", keywords: ["alaska", "ak", "united states", "akst", "akdt", "alaska time"] },
  { value: "America/Chicago", city: "Chicago", country: "USA", abbr: "CST", keywords: ["illinois", "il", "central", "central time", "cst", "cdt", "united states", "midwest", "texas", "tx", "houston", "dallas", "austin", "san antonio", "minnesota", "mn", "wisconsin", "wi", "iowa", "missouri", "kansas", "nebraska", "oklahoma", "louisiana", "arkansas", "mississippi", "alabama", "tennessee"] },
  { value: "America/Denver", city: "Denver", country: "USA", abbr: "MST", keywords: ["colorado", "co", "mountain", "mountain time", "mst", "mdt", "united states", "utah", "wyoming", "montana", "new mexico", "idaho", "salt lake city", "albuquerque"] },
  { value: "Pacific/Honolulu", city: "Honolulu", country: "USA", abbr: "HST", keywords: ["hawaii", "hi", "united states", "hst", "hawaii time"] },
  { value: "America/Los_Angeles", city: "Los Angeles", country: "USA", abbr: "PST", keywords: ["california", "ca", "pacific", "pacific time", "pst", "pdt", "united states", "la", "west coast", "san francisco", "sf", "seattle", "washington", "wa", "oregon", "nevada", "las vegas", "san diego", "hollywood"] },
  { value: "America/New_York", city: "New York", country: "USA", abbr: "EST", keywords: ["ny", "eastern", "eastern time", "est", "edt", "united states", "east coast", "nyc", "florida", "fl", "miami", "georgia", "ga", "atlanta", "north carolina", "nc", "south carolina", "sc", "virginia", "va", "maryland", "md", "massachusetts", "ma", "boston", "pennsylvania", "pa", "philadelphia", "ohio", "oh", "michigan", "mi", "detroit", "indiana", "in", "new jersey", "nj", "connecticut", "ct", "washington dc", "dc"] },
  { value: "America/Phoenix", city: "Phoenix", country: "USA", abbr: "MST", keywords: ["arizona", "az", "united states", "mst", "mountain time", "tucson"] },

  // Canada
  { value: "America/Toronto", city: "Toronto", country: "Canada", abbr: "EST", keywords: ["ontario", "on", "eastern", "eastern time", "est", "edt", "ottawa", "montreal", "quebec"] },
  { value: "America/Vancouver", city: "Vancouver", country: "Canada", abbr: "PST", keywords: ["british columbia", "bc", "pacific", "pacific time", "pst", "pdt"] },

  // Mexico & Central America
  { value: "America/Mexico_City", city: "Mexico City", country: "Mexico", abbr: "CST", keywords: ["cdmx", "central", "cst"] },

  // South America - alphabetical by city
  { value: "America/Bogota", city: "Bogota", country: "Colombia", abbr: "COT", keywords: ["bogotá"] },
  { value: "America/Buenos_Aires", city: "Buenos Aires", country: "Argentina", abbr: "ART", keywords: [] },
  { value: "America/Lima", city: "Lima", country: "Peru", abbr: "PET", keywords: [] },
  { value: "America/Santiago", city: "Santiago", country: "Chile", abbr: "CLT", keywords: [] },
  { value: "America/Sao_Paulo", city: "São Paulo", country: "Brazil", abbr: "BRT", keywords: ["sao paulo", "sp"] },

  // Europe - alphabetical by city
  { value: "Europe/Amsterdam", city: "Amsterdam", country: "Netherlands", abbr: "CET", keywords: ["holland", "nl"] },
  { value: "Europe/Athens", city: "Athens", country: "Greece", abbr: "EET", keywords: [] },
  { value: "Europe/Berlin", city: "Berlin", country: "Germany", abbr: "CET", keywords: ["de"] },
  { value: "Europe/Brussels", city: "Brussels", country: "Belgium", abbr: "CET", keywords: [] },
  { value: "Europe/Budapest", city: "Budapest", country: "Hungary", abbr: "CET", keywords: [] },
  { value: "Europe/Copenhagen", city: "Copenhagen", country: "Denmark", abbr: "CET", keywords: [] },
  { value: "Europe/Dublin", city: "Dublin", country: "Ireland", abbr: "GMT", keywords: [] },
  { value: "Europe/Istanbul", city: "Istanbul", country: "Turkey", abbr: "TRT", keywords: [] },
  { value: "Europe/Lisbon", city: "Lisbon", country: "Portugal", abbr: "WET", keywords: [] },
  { value: "Europe/London", city: "London", country: "UK", abbr: "GMT", keywords: ["england", "britain", "united kingdom", "gb"] },
  { value: "Europe/Madrid", city: "Madrid", country: "Spain", abbr: "CET", keywords: [] },
  { value: "Europe/Moscow", city: "Moscow", country: "Russia", abbr: "MSK", keywords: [] },
  { value: "Europe/Paris", city: "Paris", country: "France", abbr: "CET", keywords: ["fr"] },
  { value: "Europe/Prague", city: "Prague", country: "Czech Republic", abbr: "CET", keywords: ["czechia"] },
  { value: "Europe/Rome", city: "Rome", country: "Italy", abbr: "CET", keywords: ["it"] },
  { value: "Europe/Stockholm", city: "Stockholm", country: "Sweden", abbr: "CET", keywords: [] },
  { value: "Europe/Vienna", city: "Vienna", country: "Austria", abbr: "CET", keywords: [] },
  { value: "Europe/Warsaw", city: "Warsaw", country: "Poland", abbr: "CET", keywords: [] },

  // Asia - alphabetical by city
  { value: "Asia/Bangkok", city: "Bangkok", country: "Thailand", abbr: "ICT", keywords: [] },
  { value: "Asia/Shanghai", city: "Beijing / Shanghai", country: "China", abbr: "CST", keywords: ["beijing", "shanghai", "prc"] },
  { value: "Asia/Dubai", city: "Dubai", country: "UAE", abbr: "GST", keywords: ["united arab emirates"] },
  { value: "Asia/Ho_Chi_Minh", city: "Ho Chi Minh City", country: "Vietnam", abbr: "ICT", keywords: ["saigon"] },
  { value: "Asia/Hong_Kong", city: "Hong Kong", country: "Hong Kong", abbr: "HKT", keywords: ["hk"] },
  { value: "Asia/Jakarta", city: "Jakarta", country: "Indonesia", abbr: "WIB", keywords: [] },
  { value: "Asia/Karachi", city: "Karachi", country: "Pakistan", abbr: "PKT", keywords: [] },
  { value: "Asia/Kuala_Lumpur", city: "Kuala Lumpur", country: "Malaysia", abbr: "MYT", keywords: ["kl"] },
  { value: "Asia/Manila", city: "Manila", country: "Philippines", abbr: "PHT", keywords: [] },
  { value: "Asia/Kolkata", city: "Mumbai / Kolkata", country: "India", abbr: "IST", keywords: ["mumbai", "kolkata", "delhi", "bangalore", "chennai"] },
  { value: "Asia/Seoul", city: "Seoul", country: "South Korea", abbr: "KST", keywords: ["korea"] },
  { value: "Asia/Singapore", city: "Singapore", country: "Singapore", abbr: "SGT", keywords: ["sg"] },
  { value: "Asia/Taipei", city: "Taipei", country: "Taiwan", abbr: "CST", keywords: [] },
  { value: "Asia/Tel_Aviv", city: "Tel Aviv", country: "Israel", abbr: "IST", keywords: ["jerusalem"] },
  { value: "Asia/Tokyo", city: "Tokyo", country: "Japan", abbr: "JST", keywords: ["jp"] },

  // Australia & Pacific - alphabetical by city
  { value: "Pacific/Auckland", city: "Auckland", country: "New Zealand", abbr: "NZST", keywords: ["nz"] },
  { value: "Australia/Brisbane", city: "Brisbane", country: "Australia", abbr: "AEST", keywords: ["queensland", "qld"] },
  { value: "Pacific/Fiji", city: "Fiji", country: "Fiji", abbr: "FJT", keywords: [] },
  { value: "Australia/Melbourne", city: "Melbourne", country: "Australia", abbr: "AEST", keywords: ["victoria", "vic"] },
  { value: "Australia/Perth", city: "Perth", country: "Australia", abbr: "AWST", keywords: ["western australia", "wa"] },
  { value: "Australia/Sydney", city: "Sydney", country: "Australia", abbr: "AEST", keywords: ["nsw", "new south wales"] },

  // Africa - alphabetical by city
  { value: "Africa/Cairo", city: "Cairo", country: "Egypt", abbr: "EET", keywords: [] },
  { value: "Africa/Johannesburg", city: "Johannesburg", country: "South Africa", abbr: "SAST", keywords: ["cape town"] },
  { value: "Africa/Lagos", city: "Lagos", country: "Nigeria", abbr: "WAT", keywords: [] },
  { value: "Africa/Nairobi", city: "Nairobi", country: "Kenya", abbr: "EAT", keywords: [] },
];

/**
 * Get a comprehensive list of global timezones
 * Sorted alphabetically by city name (per NN Group UX research)
 * @returns Array of timezone options with labels, GMT offsets, and search keywords
 */
export function getCommonTimezones(): TimezoneOption[] {
  return rawTimezoneData
    .map((tz) => ({
      value: tz.value,
      label: `${tz.city}, ${tz.country}`,
      city: tz.city,
      country: tz.country,
      abbr: tz.abbr,
      gmtOffset: getGmtOffset(tz.value),
      keywords: tz.keywords,
    }))
    .sort((a, b) => a.city.localeCompare(b.city));
}

/**
 * Get timezone display name with GMT offset
 * @param timezone IANA timezone string
 * @returns Display string like "New York, USA (GMT-5)"
 */
export function getTimezoneDisplay(timezone: string): string {
  const timezones = getCommonTimezones();
  const match = timezones.find((tz) => tz.value === timezone);

  if (match) {
    return `${match.label} (${match.gmtOffset})`;
  }

  // For non-common timezones, return the timezone with dynamically calculated offset
  const offset = getGmtOffset(timezone);
  return offset ? `${timezone} (${offset})` : timezone;
}

/**
 * Get timezone abbreviation from IANA timezone string
 * @param timezone IANA timezone string
 * @returns Timezone abbreviation like "EST" or GMT offset if not found
 */
export function getTimezoneAbbreviation(timezone: string): string {
  const timezones = getCommonTimezones();
  const match = timezones.find((tz) => tz.value === timezone);
  if (match?.abbr) {
    return match.abbr;
  }
  // Fallback to dynamically calculating GMT offset
  return getGmtOffset(timezone) || timezone;
}

/**
 * Check if a timezone string is valid
 * @param timezone Timezone string to validate
 * @returns True if valid, false otherwise
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if two timezones have the same UTC offset at a given time
 * This handles cases where different timezone names refer to the same actual time
 * (e.g., "America/New_York" and "US/Eastern" during the same period)
 * @param tz1 First timezone (IANA string)
 * @param tz2 Second timezone (IANA string)
 * @param date Optional date to check (defaults to now)
 * @returns True if both timezones have the same UTC offset
 */
export function timezonesHaveSameOffset(tz1: string, tz2: string, date: Date = new Date()): boolean {
  // Quick check for exact match
  if (tz1 === tz2) return true;

  try {
    // Get the time in each timezone and compare
    const formatter1 = new Intl.DateTimeFormat("en-US", {
      timeZone: tz1,
      timeZoneName: "shortOffset",
    });
    const formatter2 = new Intl.DateTimeFormat("en-US", {
      timeZone: tz2,
      timeZoneName: "shortOffset",
    });

    const parts1 = formatter1.formatToParts(date);
    const parts2 = formatter2.formatToParts(date);

    const offset1 = parts1.find((p) => p.type === "timeZoneName")?.value;
    const offset2 = parts2.find((p) => p.type === "timeZoneName")?.value;

    return offset1 === offset2;
  } catch {
    // If comparison fails, fall back to string comparison
    return tz1 === tz2;
  }
}
